import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Diamond, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

export default function Login() {
  const { login } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success(t("auth.welcome"));
      nav("/dashboard");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-obsidian text-white">
      <div className="hidden lg:block relative grain">
        <img src="https://images.unsplash.com/photo-1624588057318-5f1b2eb81012?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000" alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/30 to-transparent" />
        <div className="relative z-10 p-12 h-full flex flex-col justify-end">
          <h2 className="font-serif text-4xl tracking-tight">{t("auth.workshopTagline")}</h2>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="flex items-center justify-between mb-10">
            <Link to="/" className="flex items-center gap-2"><img src="/hephy-logo.png" alt="Hephy 3D" className="w-8 h-8 object-contain" /><span className="font-serif text-xl">Hephy 3D</span></Link>
            <LanguageSwitcher />
          </div>
          <h1 className="text-3xl font-bold tracking-tight">{t("auth.loginTitle")}</h1>
          <p className="text-white/50 mt-2 mb-8 text-sm">{t("auth.loginSubtitle")}</p>
          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label className="text-white/70">{t("auth.email")}</Label>
              <Input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 bg-white/5 border-white/15 h-11" placeholder="you@email.com" />
            </div>
            <div>
              <Label className="text-white/70">{t("auth.password")}</Label>
              <Input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-2 bg-white/5 border-white/15 h-11" placeholder="••••••••" />
            </div>
            <Button data-testid="login-submit" disabled={loading} className="w-full h-11 rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold">
              {loading ? <CircleNotch className="animate-spin" /> : t("auth.signIn")}
            </Button>
          </form>
          <div className="flex items-center gap-3 my-5"><div className="h-px bg-white/10 flex-1" /><span className="text-xs text-white/40">o</span><div className="h-px bg-white/10 flex-1" /></div>
          <Button type="button" data-testid="login-google" onClick={() => {
            // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
            const redirectUrl = window.location.origin + "/dashboard";
            window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
          }} variant="outline" className="w-full h-11 rounded-full border-white/15 bg-white/5 hover:bg-white/10 font-medium flex items-center justify-center gap-2">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" /> Continuar con Google
          </Button>
          <p className="text-sm text-white/50 mt-6">{t("auth.noAccount")} <Link to="/register" className="text-gold hover:underline">{t("auth.signUp")}</Link></p>
        </div>
      </div>
    </div>
  );
}
